import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import Loader from '../lib/components/Loader';

const CurrentSeasonTab = ({ managerName }) => {
    const [seasonData, setSeasonData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!managerName) return;

        setLoading(true);
        setError(null);

        api.get(`/api/v1/managers/${managerName}/current-season`)
            .then(({ data }) => {
                setSeasonData(data);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error fetching current season data:', err);
                setError('Failed to load current season data');
                setLoading(false);
            });
    }, [managerName]);

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return (
            <div className="alert alert-danger">
                {error}
            </div>
        );
    }

    if (!seasonData) {
        return (
            <div className="alert alert-warning">
                No season data available
            </div>
        );
    }

    const { teamInfo = {}, record = {}, schedule = [], standings } = seasonData;

    // Calculate stats from schedule
    const completedGames = schedule.filter(game => game.result !== 'pending');
    const totalPointsScored = completedGames.reduce((sum, game) => sum + (game.myScore || 0), 0);
    const totalPointsAgainst = completedGames.reduce((sum, game) => sum + (game.opponentScore || 0), 0);
    const avgPointsScored = completedGames.length > 0 ? (totalPointsScored / completedGames.length).toFixed(2) : 0;
    const avgPointsAgainst = completedGames.length > 0 ? (totalPointsAgainst / completedGames.length).toFixed(2) : 0;

    // Get standings context (2 teams above and 2 teams below)
    const managerRank = record.rank;
    const standingsContext = standings ? standings.filter((team, index) => {
        const teamRank = team.rank;
        return teamRank >= managerRank - 2 && teamRank <= managerRank + 2;
    }) : [];

    return (
        <div className="current-season-tab">
            {/* Team Header */}
            <div className="card bg-dark text-white mb-4">
                <div className="card-body">
                    <div className="row align-items-center">
                        <div className="col-md-2 text-center">
                            {teamInfo.teamLogo && (
                                <img 
                                    src={teamInfo.teamLogo} 
                                    alt={teamInfo.teamName}
                                    className="img-fluid rounded"
                                    style={{ maxHeight: '100px' }}
                                />
                            )}
                        </div>
                        <div className="col-md-10">
                            <h2 className="mb-2 text-center">{teamInfo.teamName}</h2>
                            <div className="row">
                                <div className="col-6 col-md-3">
                                    <div className="text-center">
                                        <small className="text-muted d-block">Record</small>
                                        <strong className="h5">
                                            {record.wins}-{record.losses}
                                            {record.ties > 0 && `-${record.ties}`}
                                        </strong>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="text-center">
                                        <small className="text-muted d-block">Rank</small>
                                        <strong className="h5">#{record.rank}</strong>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="text-center">
                                        <small className="text-muted d-block">Points For</small>
                                        <strong className="h5">{totalPointsScored.toFixed(2)}</strong>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="text-center">
                                        <small className="text-muted d-block">Avg Points/Game</small>
                                        <strong className="h5">{avgPointsScored}</strong>
                                    </div>
                                </div>
                            </div>
                            {teamInfo.yahooTeamUrl && (
                                <div className="mt-3">
                                    <a 
                                        href={teamInfo.yahooTeamUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-light"
                                    >
                                        View on Yahoo <i className="fas fa-external-link-alt"></i>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Standings Preview */}
            {standingsContext.length > 0 && (
                <div className="card bg-dark text-white mb-4">
                    <div className="card-header bg-dark">
                        <h4 className="mb-0">Standings</h4>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-dark table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th className="ps-3">Rank</th>
                                        <th>Team</th>
                                        <th className="text-center">Record</th>
                                        <th className="text-end pe-3">PF</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {standingsContext.map((team) => {
                                        const isCurrentManager = team.manager === managerName;
                                        return (
                                            <tr 
                                                key={team.manager}
                                                className={isCurrentManager ? 'table-primary' : ''}
                                                style={isCurrentManager ? { 
                                                    backgroundColor: 'rgba(13, 110, 253, 0.25)',
                                                    fontWeight: 'bold'
                                                } : {}}
                                            >
                                                <td className="ps-3">
                                                    <strong>#{team.rank}</strong>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        {team.teamLogo && (
                                                            <img 
                                                                src={team.teamLogo} 
                                                                alt={team.manager}
                                                                className="rounded me-2"
                                                                style={{ width: '30px', height: '30px' }}
                                                            />
                                                        )}
                                                        <div>
                                                            <div>
                                                                {team.manager}
                                                            </div>
                                                            <small className={`${isCurrentManager ? 'text-black' : 'text-muted'}`}>{team.teamName}</small>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    {team.wins}-{team.losses}
                                                    {team.ties > 0 && `-${team.ties}`}
                                                </td>
                                                <td className="text-end pe-3">
                                                    {team.pointsFor.toFixed(2)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Schedule */}
            <div className="card bg-dark text-white">
                <div className="card-header bg-dark">
                    <h3 className="mb-0">Schedule</h3>
                </div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-dark table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Week</th>
                                    <th>Opponent</th>
                                    <th className="text-center">Result</th>
                                    <th className="text-center">Score</th>
                                    <th className="text-center">Opponent</th>
                                </tr>
                            </thead>
                            <tbody>
                                {schedule.map((game) => {
                                    let resultBadge = '';
                                    let resultClass = '';
                                    
                                    if (game.result === 'win') {
                                        resultBadge = 'W';
                                        resultClass = 'badge bg-success';
                                    } else if (game.result === 'loss') {
                                        resultBadge = 'L';
                                        resultClass = 'badge bg-danger';
                                    } else if (game.result === 'tie') {
                                        resultBadge = 'T';
                                        resultClass = 'badge bg-warning';
                                    } else {
                                        resultBadge = '-';
                                        resultClass = 'badge bg-secondary';
                                    }

                                    return (
                                        <tr key={game.week}>
                                            <td>
                                                <strong>{game.week}</strong>
                                                {game.isPlayoffs && (
                                                    <span className="badge bg-info ms-2">Playoffs</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    {game.opponentLogo && (
                                                        <img 
                                                            src={game.opponentLogo} 
                                                            alt={game.opponent}
                                                            className="rounded me-2"
                                                            style={{ width: '30px', height: '30px' }}
                                                        />
                                                    )}
                                                    <div>
                                                        <div><strong>{game.opponent}</strong></div>
                                                        <small className="text-muted">{game.opponentTeamName}</small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <span className={resultClass}>{resultBadge}</span>
                                            </td>
                                            <td className="text-center">
                                                {game.myScore !== null ? (
                                                    <span className={game.result === 'win' ? 'text-success fw-bold' : ''}>
                                                        {game.myScore.toFixed(2)}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">
                                                        {game.myProjectedScore ? `(${game.myProjectedScore.toFixed(2)})` : '-'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {game.opponentScore !== null ? (
                                                    <span className={game.result === 'loss' ? 'text-danger fw-bold' : ''}>
                                                        {game.opponentScore.toFixed(2)}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">
                                                        {game.opponentProjectedScore ? `(${game.opponentProjectedScore.toFixed(2)})` : '-'}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Additional Stats */}
            <div className="card bg-dark text-white mt-4">
                <div className="card-header">
                    <h4 className="mb-0">Season Stats</h4>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <div className="text-center p-3 border rounded">
                                <small className="text-muted d-block">Games Played</small>
                                <strong className="h4">{completedGames.length}</strong>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-center p-3 border rounded">
                                <small className="text-muted d-block">Total Points For</small>
                                <strong className="h4">{totalPointsScored.toFixed(2)}</strong>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-center p-3 border rounded">
                                <small className="text-muted d-block">Total Points Against</small>
                                <strong className="h4">{totalPointsAgainst.toFixed(2)}</strong>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-center p-3 border rounded">
                                <small className="text-muted d-block">Avg Points Against</small>
                                <strong className="h4">{avgPointsAgainst}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CurrentSeasonTab;
